import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { GraduationCap, BookOpen, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Mundalo - Classroom (iter 50 rebrand).
 *
 * Restored: tab-based navigation by topic (Visas / Taxes / Housing /
 * Healthcare / Moving / All). Cards now use the cream-on-cream Mundalo
 * design language, no more dark blocks.
 */
const TOPIC_TABS = [
  { id: 'all',         label: 'All',        match: () => true },
  { id: 'visas',       label: 'Visas',      match: (m) => /visa|immigr|residen/i.test(`${m.category} ${m.title}`) },
  { id: 'taxes',       label: 'Taxes',      match: (m) => /tax/i.test(`${m.category} ${m.title}`) },
  { id: 'housing',     label: 'Housing',    match: (m) => /hous|rent|prop|neighborhood/i.test(`${m.category} ${m.title}`) },
  { id: 'healthcare',  label: 'Healthcare', match: (m) => /health|medical|insurance|doctor/i.test(`${m.category} ${m.title}`) },
  { id: 'moving',      label: 'Moving',     match: (m) => /mov|ship|logist|first.?30|first.?day/i.test(`${m.category} ${m.title}`) },
  { id: 'finance',     label: 'Finance',    match: (m) => /financ|bank|money|wire|currency/i.test(`${m.category} ${m.title}`) },
];

const ModuleCard = ({ module: m }) => (
  <Link
    key={m.module_id}
    to={`/portal/classroom/${m.module_id}`}
    className="bg-white border border-[#EFE6CF] hover:border-[#2CA8A0]/50 rounded-xl overflow-hidden transition group block"
    data-testid={`module-card-${m.module_id}`}
  >
    {/* 16:9 thumbnail */}
    <div className="aspect-video bg-[#F4ECD8] overflow-hidden">
      {m.thumbnail_url ? (
        <img
          src={m.thumbnail_url}
          alt={m.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <GraduationCap className="w-12 h-12 text-[#1A5C6E]/30" />
        </div>
      )}
    </div>
    <div className="p-5">
      {m.category && (
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] mb-2">
          {m.category}
        </p>
      )}
      <h3 className="font-display italic text-xl text-[#0A1F2E] mb-2 group-hover:text-[#1A5C6E] transition leading-tight">
        {m.title}
      </h3>
      {m.description && (
        <p className="text-[#0A1F2E]/70 text-sm mb-4 line-clamp-2 leading-snug">
          {m.description.length > 120
            ? `${m.description.substring(0, 120)}…`
            : m.description}
        </p>
      )}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#0A1F2E]/55 flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            {m.completed_count}/{m.lesson_count} lessons
          </span>
          <span className="font-mono uppercase tracking-[0.12em] text-[#2CA8A0]">
            {m.progress_percent ?? 0}%
          </span>
        </div>
        <div className="h-1 bg-[#F4ECD8] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#2CA8A0] rounded-full transition-all duration-300"
            style={{ width: `${m.progress_percent ?? 0}%` }}
          />
        </div>
      </div>
    </div>
  </Link>
);

const PortalClassroom = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/classroom/modules`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error('Failed to fetch modules');
        const data = await r.json();
        setModules(data.modules || []);
      } catch (err) {
        console.error('Error fetching modules:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const matcher = TOPIC_TABS.find((t) => t.id === activeTab)?.match || (() => true);
    return modules.filter(matcher);
  }, [modules, activeTab]);

  // Only show a topic tab if at least one module matches (besides "All").
  const visibleTabs = useMemo(() => {
    return TOPIC_TABS.filter((t) => t.id === 'all' || modules.some(t.match));
  }, [modules]);

  return (
    <PortalLayout activeTab="classroom">
      <SEO title="Classroom - Mundalo" description="Video courses and guides to help you relocate successfully." path="/portal/classroom" />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="portal-classroom-page">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            The Move · Classroom
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Learn the move.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Video courses and guides to help you relocate successfully - visas, taxes, housing, healthcare, moving and more.
          </p>
        </header>

        {/* Topic tabs */}
        {modules.length > 0 && (
          <div
            role="tablist"
            aria-label="Classroom topics"
            className="flex flex-wrap gap-2 mb-8"
            data-testid="classroom-topic-tabs"
          >
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activeTab === t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`classroom-tab-${t.id}`}
                className={`font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full border transition ${
                  activeTab === t.id
                    ? 'bg-[#0A1F2E] text-white border-[#0A1F2E]'
                    : 'bg-white text-[#0A1F2E]/75 border-[#EFE6CF] hover:border-[#0A1F2E]/40'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 mb-6 rounded-xl text-sm">
            {error}
          </div>
        )}

        {!loading && !error && modules.length === 0 && (
          <div
            className="bg-white border border-[#EFE6CF] p-12 text-center rounded-xl"
            data-testid="classroom-empty-state"
          >
            <GraduationCap className="w-12 h-12 text-[#1A5C6E]/40 mx-auto mb-4" />
            <p className="text-[#0A1F2E]/60">No courses available yet. Check back soon!</p>
          </div>
        )}

        {!loading && !error && modules.length > 0 && filtered.length === 0 && (
          <p className="font-body text-base text-[#0A1F2E]/55 text-center py-12">
            No modules in this topic yet.
          </p>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((m) => (
              <ModuleCard key={m.module_id} module={m} />
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalClassroom;
